
import type { CampaignInfluencer } from '@/marketing/models/campaignInfluencer';
import type {
  CampaignScoutingRow,
  ScoutingPageDetails,
  ScoutingSelectionCandidate,
} from '@/marketing/models/campaignScouting';
export const SCOUTING_FILTERS = ['All', 'Applied', 'Maybe', 'Finalist', 'Selected', 'Rejected'] as const;
export type ScoutingFilter = (typeof SCOUTING_FILTERS)[number];
const SELECTED_STAGES = ['Shortlisted', 'Brand Shortlisted', 'Shipped', 'Received', 'Published', 'Completed'];
export interface ScoutingViewRow extends Omit<CampaignScoutingRow, 'details'> {
  details?: ScoutingPageDetails;
  creator?: CampaignInfluencer;
}
export interface ScoutingCandidateSnapshot extends ScoutingSelectionCandidate {
  handle?: string;
}
export function scoutingStatus(row: Pick<CampaignScoutingRow, 'decision' | 'pipelineStage' | 'rejected'>): string {
  if (row.rejected || row.decision === 'Declined') {
    return 'Rejected';
  }
  if (SELECTED_STAGES.includes(row.pipelineStage)) {
    return 'Selected';
  }
  if (row.pipelineStage !== 'Form Sent') {
    return row.pipelineStage;
  }
  return row.decision ?? 'Applied';
}
export function canReviewScouting(row: Pick<CampaignScoutingRow, 'decision' | 'pipelineStage' | 'rejected'>): boolean {
  return row.pipelineStage === 'Form Sent' && !row.rejected && row.decision !== 'Declined';
}
export function isSelectableFinalist(
  row: Pick<CampaignScoutingRow, 'decision' | 'pipelineStage' | 'rejected'>,
): boolean {
  return canReviewScouting(row) && row.decision === 'Finalist';
}
export function joinScoutingRows(
  campaignId: string,
  rows: CampaignScoutingRow[],
  creators: CampaignInfluencer[],
): ScoutingViewRow[] {
  const byId = new Map(
    creators.filter((creator) => creator.campaignId === campaignId).map((creator) => [creator.influencerId, creator]),
  );
  return rows.map((row) => {
    const creator = byId.get(row.influencerId);
    return { ...row, ...(creator ? { creator } : {}) };
  });
}
export function filterScoutingRows(rows: ScoutingViewRow[], filter: ScoutingFilter, search: string): ScoutingViewRow[] {
  const term = search.trim().toLowerCase();
  return rows.filter(
    (row) =>
      (filter === 'All' || scoutingStatus(row) === filter) &&
      (!term ||
        [row.influencerId, row.creator?.handle, row.creator?.platform].some((value) =>
          value?.toLowerCase().includes(term),
        )),
  );
}
export function snapshotScoutingCandidates(rows: ScoutingViewRow[], checked: Set<string>): ScoutingCandidateSnapshot[] {
  return rows
    .filter((row) => checked.has(row.influencerId) && isSelectableFinalist(row))
    .map((row) => ({
      influencerId: row.influencerId,
      expectedVersion: row.version,
      ...(row.creator?.handle ? { handle: row.creator.handle } : {}),
    }));
}
